import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { styled } from "@material-ui/styles";
import { Button, IconButton, CircularProgress } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SportsSoccerIcon from "@material-ui/icons/SportsSoccer";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { injectIntl } from "react-intl";
import { END_MATCH, UPDATE_STATUS } from "./MatchQueries";
import messages from "./PageViewMatch.intl";
import { IStyledArguments } from "../../types/styled-arguments";
import { IPlayer } from "../../types";

/*
 * Modo "Resultado rápido": una sola pantalla para anotar goles por jugador
 * y marcador del rival, sin convocatoria ni alineación. El marcador propio
 * se autocalcula (goles de jugadores + goles en propia del rival).
 */

const Card = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2.5),
  [theme.breakpoints.down("sm")]: {
    borderRadius: 14,
    padding: theme.spacing(2),
  },
}));

const SectionTitle = styled("h3")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
  fontSize: "1.1rem",
  color: theme.palette.text.primary,
  margin: 0,
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  "& svg": {
    color: theme.palette.primary.main,
  },
}));

/*
 * Grid de 3 columnas x 3 filas (nombre / marcador / controles). Cada elemento
 * ocupa su propia celda para que nombres y números queden alineados entre
 * equipos, aunque solo el rival tenga controles (el marcador propio se
 * autocalcula desde los goles).
 */
const ScoreGrid = styled("div")(({ theme }: IStyledArguments) => ({
  display: "grid",
  gridTemplateColumns: "1fr auto 1fr",
  alignItems: "center",
  justifyItems: "center",
  columnGap: theme.spacing(2),
}));

const TeamName = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 500,
  fontSize: "0.9rem",
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const ScoreValue = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 800,
  fontSize: "3rem",
  lineHeight: 1,
  fontVariantNumeric: "tabular-nums",
  color: theme.palette.text.primary,
}));

const ScoreSeparator = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "2rem",
  color: theme.palette.text.secondary,
}));

const CounterControls = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1.5),
}));

const CounterButton = styled(IconButton)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  padding: 6,
  "&:hover": {
    background: theme.palette.extra.accentSoft,
  },
  "& svg": {
    fontSize: "1.1rem",
  },
}));

/* Ocupa la celda de controles del equipo local, cuyo marcador no se edita a mano */
const AutoBadge = styled("div")(({ theme }: IStyledArguments) => ({
  marginTop: theme.spacing(1.5),
  padding: theme.spacing(0.5, 1.5),
  borderRadius: 8,
  background: theme.palette.extra.backgroundGrey,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  fontFamily: theme.typography.alternateFontFamily,
  fontSize: "0.7rem",
  fontWeight: 600,
  letterSpacing: 0.5,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
}));

const AutoHint = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.25, 2),
  background: theme.palette.extra.accentSoft,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 10,
  fontSize: "0.85rem",
  color: theme.palette.primary.dark,
  "& svg": {
    fontSize: "1.1rem",
    flexShrink: 0,
  },
}));

const PlayerRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.25, 2),
  background: theme.palette.extra.backgroundGrey,
  borderRadius: 10,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  marginBottom: theme.spacing(1),
}));

const PlayerIdentity = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  minWidth: 0,
}));

const PlayerAvatar = styled("div")(({ theme }: IStyledArguments) => ({
  width: 36,
  height: 36,
  borderRadius: "50%",
  background: theme.palette.extra.accentSoft,
  color: theme.palette.primary.dark,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
  fontSize: "0.8rem",
  flexShrink: 0,
}));

const PlayerName = styled("div")(({ theme }: IStyledArguments) => ({
  fontWeight: 500,
  fontSize: "0.95rem",
  color: theme.palette.text.primary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const GoalCounter = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  flexShrink: 0,
}));

const GoalCount = styled("div")<{ $active?: boolean }>(
  ({ theme, $active }: any) => ({
    minWidth: 28,
    textAlign: "center",
    fontFamily: theme.typography.alternateFontFamily,
    fontWeight: 700,
    fontSize: "1.1rem",
    fontVariantNumeric: "tabular-nums",
    color: $active ? theme.palette.primary.dark : theme.palette.text.secondary,
  })
);

const ErrorBanner = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.25, 2),
  background: "rgba(176, 0, 32, 0.06)",
  border: "1px solid rgba(176, 0, 32, 0.25)",
  borderRadius: 10,
  fontSize: "0.85rem",
  color: theme.palette.error.main,
  "& svg": {
    fontSize: "1.1rem",
    flexShrink: 0,
  },
}));

const ActionsRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: theme.spacing(2),
  marginTop: theme.spacing(1),
  flexWrap: "wrap",
}));

const initials = (player: IPlayer) =>
  `${(player.name || "?").charAt(0)}${(player.surname || "").charAt(
    0
  )}`.toUpperCase();

interface ISimpleMatchResult {
  matchId: string;
  match: any;
  onBack: () => void;
  onSaved: () => void;
}

const SimpleMatchResult = injectIntl<ISimpleMatchResult>(
  ({ matchId, match, onBack, onSaved, intl }) => {
    const [goals, setGoals] = useState<{ [playerId: string]: number }>({});
    const [ownGoals, setOwnGoals] = useState(0);
    const [awayScore, setAwayScore] = useState(0);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(false);
    const [endMatch] = useMutation(END_MATCH);
    const [updateStatus] = useMutation(UPDATE_STATUS);

    const squad: IPlayer[] = match?.homeTeam?.squad || [];
    const playerGoals = Object.keys(goals).reduce(
      (acc, key) => acc + goals[key],
      0
    );
    const homeScore = playerGoals + ownGoals;

    const changeGoals = (playerId: string, delta: number) => {
      setGoals((prev) => ({
        ...prev,
        [playerId]: Math.max(0, (prev[playerId] || 0) + delta),
      }));
    };

    const handleSave = async () => {
      setSaving(true);
      setSaveError(false);
      let statusMoved = false;
      try {
        const scorerList = Object.keys(goals)
          .filter((playerId) => goals[playerId] > 0)
          .map((playerId) => ({ playerId, value: goals[playerId] }));

        /*
         * El backend solo persiste el resultado si el partido está ONGOING:
         * endMatch ignora en silencio cualquier otro estado y devuelve el
         * partido intacto. En el modo rápido venimos de PREPARING, así que
         * hay que mover el estado antes de cerrar el partido.
         */
        await updateStatus({ variables: { matchId, status: "ONGOING" } });
        statusMoved = true;

        const { data } = await endMatch({
          variables: {
            matchId,
            scoreHome: homeScore,
            scoreAway: awayScore,
            scorerList,
            ownGoals,
            admonisheds: [],
          },
        });

        // Sin FINISHED no se ha guardado nada: no demos el cierre por bueno.
        if (data?.endMatch?.status !== "FINISHED") {
          throw new Error("endMatch did not finish the match");
        }
        onSaved();
      } catch (error) {
        // Evita dejar el partido atascado en ONGOING si el cierre falló.
        if (statusMoved) {
          try {
            await updateStatus({
              variables: { matchId, status: "PREPARING" },
            });
          } catch (rollbackError) {
            // Sin conexión no hay rollback posible; el error ya se muestra.
          }
        }
        setSaveError(true);
        setSaving(false);
      }
    };

    return (
      <>
        <Card>
          <SectionTitle>
            <SportsSoccerIcon />
            {intl.formatMessage(messages.quickResultTitle)}
          </SectionTitle>
          <ScoreGrid>
            <TeamName>{match.homeTeam?.name}</TeamName>
            <div />
            <TeamName>{match.awayTeam}</TeamName>

            <ScoreValue data-testid="simple-home-score">{homeScore}</ScoreValue>
            <ScoreSeparator>–</ScoreSeparator>
            <ScoreValue data-testid="simple-away-score">{awayScore}</ScoreValue>

            <AutoBadge>
              {intl.formatMessage(messages.quickResultAutoBadge)}
            </AutoBadge>
            <div />
            <CounterControls>
              <CounterButton
                aria-label="remove away goal"
                onClick={() => setAwayScore(Math.max(0, awayScore - 1))}
                disabled={awayScore === 0}
              >
                <RemoveIcon />
              </CounterButton>
              <CounterButton
                aria-label="add away goal"
                onClick={() => setAwayScore(awayScore + 1)}
              >
                <AddIcon />
              </CounterButton>
            </CounterControls>
          </ScoreGrid>
          <AutoHint>
            <InfoOutlinedIcon />
            {intl.formatMessage(messages.quickResultAutoScore)}
          </AutoHint>
        </Card>

        <Card>
          <SectionTitle>
            {intl.formatMessage(messages.quickResultScorers)}
          </SectionTitle>
          {squad.map((player) => (
            <PlayerRow key={player.id}>
              <PlayerIdentity>
                <PlayerAvatar>{initials(player)}</PlayerAvatar>
                <PlayerName>
                  {player.name} {player.surname}
                </PlayerName>
              </PlayerIdentity>
              <GoalCounter>
                <CounterButton
                  aria-label={`remove goal ${player.id}`}
                  onClick={() => changeGoals(player.id, -1)}
                  disabled={!goals[player.id]}
                >
                  <RemoveIcon />
                </CounterButton>
                <GoalCount $active={(goals[player.id] || 0) > 0}>
                  {goals[player.id] || 0}
                </GoalCount>
                <CounterButton
                  aria-label={`add goal ${player.id}`}
                  onClick={() => changeGoals(player.id, 1)}
                >
                  <AddIcon />
                </CounterButton>
              </GoalCounter>
            </PlayerRow>
          ))}

          <PlayerRow>
            <PlayerIdentity>
              <PlayerAvatar>OG</PlayerAvatar>
              <PlayerName>
                {intl.formatMessage(messages.quickResultOwnGoals)}
              </PlayerName>
            </PlayerIdentity>
            <GoalCounter>
              <CounterButton
                aria-label="remove own goal"
                onClick={() => setOwnGoals(Math.max(0, ownGoals - 1))}
                disabled={ownGoals === 0}
              >
                <RemoveIcon />
              </CounterButton>
              <GoalCount $active={ownGoals > 0}>{ownGoals}</GoalCount>
              <CounterButton
                aria-label="add own goal"
                onClick={() => setOwnGoals(ownGoals + 1)}
              >
                <AddIcon />
              </CounterButton>
            </GoalCounter>
          </PlayerRow>
        </Card>

        {saveError && (
          <ErrorBanner role="alert" data-testid="quick-result-error">
            <ErrorOutlineIcon />
            {intl.formatMessage(messages.quickResultSaveError)}
          </ErrorBanner>
        )}

        <ActionsRow>
          <Button
            variant="outlined"
            color="default"
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            disabled={saving}
          >
            {intl.formatMessage(messages.quickResultBack)}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSave}
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={18} color="inherit" /> : undefined
            }
          >
            {intl.formatMessage(messages.quickResultSave)}
          </Button>
        </ActionsRow>
      </>
    );
  }
);

export default SimpleMatchResult;
